import { useCallback } from 'react';

import {
  USERGROUP_REFERENCE_DEFAULT_FIELDS,
  resolveDefaultedValue,
} from '../utils/usergroupReferenceDefaults';

/**
 * Prefills an order form's reference inputs from the selected cost center (TUL-5597).
 *
 * Returns a **stable** `applyReferenceDefaults({ group, previousGroup, currentValues })` to
 * call wherever a cost center becomes selected. The current field values and the previously
 * selected cost center are passed in per call rather than captured from `formData`, so the
 * callback identity never changes: it can be listed in an effect's dependency array without
 * the effect re-running on every keystroke.
 *
 * `previousGroup` is what tells a value the customer typed apart from one only a previous
 * cost center put there — a field still holding `previousGroup`'s default is a leftover and
 * gets replaced, anything else is the customer's and survives. It is the caller's currently
 * selected group (`formData.costAllocationGroup`, which carries the two default fields by
 * design) rather than a ref owned by this hook, because the wizards render one step at a
 * time: `PrebookingWizard` unmounts the invoicing step on every step change, which would
 * wipe a ref and make the next cost center switch mistake an earlier prefill for a value
 * the customer typed.
 *
 * `group` and `previousGroup` may both be absent — a cost center whose defaults could not be
 * resolved is treated as having none, which clears a stale default rather than leaving
 * another cost center's reference on screen.
 *
 * `enabled` is false for wizard modes that show a stored order (edit / view), where the
 * value already on the order wins over any cost center default.
 */
const useUsergroupReferenceDefaults = ({
  updateField,
  enabled = true,
  fields = USERGROUP_REFERENCE_DEFAULT_FIELDS,
}) => useCallback(({ group, previousGroup, currentValues = {} }) => {
  if (!enabled) return;

  fields.forEach(({ formField, groupField }) => {
    const currentValue = currentValues[formField] ?? '';
    const resolvedValue = resolveDefaultedValue(
      currentValue,
      previousGroup?.[groupField],
      group?.[groupField]
    );

    if (resolvedValue !== currentValue) {
      updateField(formField, resolvedValue);
    }
  });
}, [updateField, enabled, fields]);

export default useUsergroupReferenceDefaults;
